// Recursive JSON tree with expand/collapse and copy-on-click.
// Renders primitives inline; arrays/objects with indent + chevron toggle.
// maxLength caps display of long string values to prevent layout thrash on
// large context payloads; click-to-expand reveals full text.
import { useState, useCallback } from "react";
import { ChevronRight, ChevronDown, Copy, Check } from "lucide-react";
import { cn } from "@/utils";

export interface JsonViewerProps {
  value: unknown;
  defaultExpandDepth?: number;
  maxLength?: number;
  className?: string;
}

interface NodeProps {
  nodeKey?: string | number | null;
  value: unknown;
  depth: number;
  defaultExpandDepth: number;
  maxLength: number;
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard not available in restricted contexts — silently ignore
    }
  }, [text]);

  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); copy(); }}
      className="ml-1 inline-flex items-center opacity-0 group-hover:opacity-100 transition-opacity text-[var(--muted-foreground)] hover:text-[var(--foreground)]"
      aria-label="Copy value"
    >
      {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
    </button>
  );
}

function StringNode({ value, maxLength }: { value: string; maxLength: number }) {
  const [expanded, setExpanded] = useState(false);
  const isLong = value.length > maxLength;
  const display = isLong && !expanded ? value.slice(0, maxLength) + "…" : value;

  return (
    <span className="group">
      <span className="text-green-600 dark:text-green-400">
        &ldquo;
        <span
          className={cn(isLong && "cursor-pointer underline decoration-dotted")}
          onClick={isLong ? () => setExpanded((e) => !e) : undefined}
        >
          {display}
        </span>
        &rdquo;
      </span>
      <CopyButton text={value} />
    </span>
  );
}

function PrimitiveNode({ value }: { value: number | boolean | null }) {
  const str = value === null ? "null" : String(value);
  const color = value === null
    ? "text-[var(--muted-foreground)]"
    : typeof value === "boolean"
    ? "text-blue-600 dark:text-blue-400"
    : "text-orange-600 dark:text-orange-400";

  return (
    <span className={cn("group", color)}>
      {str}
      <CopyButton text={str} />
    </span>
  );
}

function TreeNode({ nodeKey, value, depth, defaultExpandDepth, maxLength }: NodeProps) {
  const [open, setOpen] = useState(depth < defaultExpandDepth);
  const isObj = typeof value === "object" && value !== null;
  const entries = isObj ? Object.entries(value as Record<string, unknown>) : null;
  const isArray = Array.isArray(value);
  const count = entries?.length ?? 0;

  const keyEl = nodeKey !== null && nodeKey !== undefined ? (
    <span className="text-[var(--muted-foreground)] text-[11px] font-medium mr-1">
      {typeof nodeKey === "string" ? `"${nodeKey}"` : nodeKey}:
    </span>
  ) : null;

  if (!isObj) {
    return (
      <div className="flex items-center text-xs leading-5 pl-1">
        {keyEl}
        {typeof value === "string"
          ? <StringNode value={value} maxLength={maxLength} />
          : <PrimitiveNode value={value as number | boolean | null} />}
      </div>
    );
  }

  const bracket = isArray ? ["[", "]"] : ["{", "}"];

  return (
    <div className="text-xs leading-5">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-0.5 hover:bg-[var(--muted)] rounded px-0.5 -ml-0.5 w-full text-left"
      >
        {open
          ? <ChevronDown className="size-3 shrink-0 text-[var(--muted-foreground)]" />
          : <ChevronRight className="size-3 shrink-0 text-[var(--muted-foreground)]" />}
        {keyEl}
        <span className="text-[var(--foreground)]">{bracket[0]}</span>
        {!open && (
          <span className="text-[var(--muted-foreground)] ml-1">
            {count} {isArray ? "items" : "keys"}
          </span>
        )}
        {!open && <span className="text-[var(--foreground)]">{bracket[1]}</span>}
      </button>
      {open && (
        <div className="ml-4 border-l border-[var(--border)] pl-2">
          {entries!.map(([k, v], i) => (
            <TreeNode
              key={i}
              nodeKey={isArray ? i : k}
              value={v}
              depth={depth + 1}
              defaultExpandDepth={defaultExpandDepth}
              maxLength={maxLength}
            />
          ))}
          <div className="text-[var(--foreground)]">{bracket[1]}</div>
        </div>
      )}
    </div>
  );
}

export function JsonViewer({ value, defaultExpandDepth = 2, maxLength = 200, className }: JsonViewerProps) {
  return (
    <div className={cn("font-mono text-[11px] overflow-auto", className)}>
      <TreeNode
        nodeKey={null}
        value={value}
        depth={0}
        defaultExpandDepth={defaultExpandDepth}
        maxLength={maxLength}
      />
    </div>
  );
}

export default JsonViewer;
